import React, { useState, useEffect } from 'react';
import CompensationForm from './Compensation';
import { FileBadge2, X, ArrowLeft, Filter, Download, FileText, ChevronDown, ChevronUp, MapPin, Globe, Loader, AlertCircle, Plus, Edit2, Eye, Calendar, User, Home, CreditCard, Building } from 'lucide-react';
import apiService from '../config';
import { useLanguage } from "../StoreContext/LanguageContext";
import * as XLSX from 'xlsx';

const Payment = ({ onBack }) => {
  const { language, translations } = useLanguage();

  // State for location data
  const [districts, setDistricts] = useState([]);
  const [availableTalukas, setAvailableTalukas] = useState([]);
  const [availableVillages, setAvailableVillages] = useState([]);

  // State for land data
  const [landData, setLandData] = useState([]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedTaluka, setSelectedTaluka] = useState('');
  const [selectedVillage, setSelectedVillage] = useState('');

  // Filter mode and panel states
  const [filterMode, setFilterMode] = useState('all');
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Loading states
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingTalukas, setLoadingTalukas] = useState(false);
  const [loadingVillages, setLoadingVillages] = useState(false);
  const [loadingLandData, setLoadingLandData] = useState(false);

  // Error state
  const [error, setError] = useState('');

  // Alert state - now supports different types
  const [alert, setAlert] = useState({ type: '', message: '', show: false });

  // Form modal state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedLandRecord, setSelectedLandRecord] = useState(null);
  const [selectedcompensation, setselectedcompensation] = useState(null);

  // View modal state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewLandRecord, setViewLandRecord] = useState(null);

  // Project ID and Project details from GovDashboard
  const [projectId, setProjectId] = useState('');
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [currentProjectName, setCurrentProjectName] = useState('');
  const [proposalId, setProposalId] = useState('');

  const ROLES = {
    COLLECTOR: 1,
    LAO: 3,
    SAMANVAY: 6,
    PROJECT_INCHARGE: 18,
    DATA_ENTRY: 14
  };

  const userData = JSON.parse(localStorage.getItem('userData'));
  const userRole = parseInt(userData?.data?.user_role || userData?.user_role, 10);

  // Helper function to show alerts (excluding errors)
  const showAlert = (type, message, duration = 5000) => {
    if (type === 'error') return;

    setAlert({ type, message, show: true });
    if (duration > 0) {
      setTimeout(() => {
        setAlert(prev => ({ ...prev, show: false }));
      }, duration);
    }
  };

  // Helper function to dismiss alert
  const dismissAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  // Get project Id and Proposal Id
  useEffect(() => {
    const getProjectId = () => {
      const currentProjectData = localStorage.getItem("currentProjectData");
      if (currentProjectData) {
        const projectData = JSON.parse(currentProjectData);
        return projectData.project_id || projectData.id || "";
      }

      const userData = localStorage.getItem("userData");
      if (userData) {
        const user = JSON.parse(userData);
        if (user.default_project) return user.default_project;

        let projectId = user.data?.project_id || user.project_id;
        if (typeof projectId === "string" && projectId.includes(",")) {
          projectId = projectId.split(",")[0].trim();
        }
        return projectId || "";
      }

      return "";
    };

    const getProposalId = () => {
      const locationInfo = localStorage.getItem("userData");
      if (locationInfo) {
        try {
          const parsed = JSON.parse(locationInfo);
          const proposalId = parsed.default_proposal;
          if (proposalId) return proposalId;
        } catch (e) {
          console.error("Error parsing selectedLocationInfo:", e);
        }
      }

      const userData = localStorage.getItem("userData");
      if (userData) {
        const user = JSON.parse(userData);
        if (user.default_proposal) return user.default_proposal;
      }

      return "";
    };

    const getProjectInfo = () => {
      try {
        const selectedProjectData = localStorage.getItem('selectedProject');
        if (selectedProjectData) {
          const selectedProject = JSON.parse(selectedProjectData);
          const projectName = selectedProject.project_name || selectedProject.PROJECT_NAME || '';
          setCurrentProjectName(projectName);
        }
      } catch (error) {
        console.error('Error loading project info:', error);
      }
    };

    setProjectId(getProjectId());
    setProposalId(getProposalId());
    getProjectInfo();
  }, []);

  // Load districts on component mount
  useEffect(() => {
    loadDistricts();
  }, []);

  // Load districts from API
  const loadDistricts = async () => {
    try {
      setLoadingDistricts(true);
      setError('');
      dismissAlert();

      const response = await apiService.getDistricts();
      console.log('Districts response:', response);

      const districtsData = response.data || response || [];
      const districtsArray = Array.isArray(districtsData) ? districtsData : [];
      setDistricts(districtsArray);

      if (districtsArray.length > 0) {
        showAlert('success', `Successfully loaded ${districtsArray.length} districts`, 3000);
      } else {
        showAlert('warning', 'No districts found in the system');
      }

    } catch (error) {
      console.error('Error loading districts:', error);
      setError('Failed to load districts: ' + error.message);
      setDistricts([]);
    } finally {
      setLoadingDistricts(false);
    }
  };

  // Load talukas when district changes
  useEffect(() => {
    if (selectedDistrict) {
      loadTalukas();
    } else {
      setAvailableTalukas([]);
    }
    if (selectedDistrict && availableTalukas.length > 0) {
      setSelectedTaluka('');
      setSelectedVillage('');
      setAvailableVillages([]);
      setLandData([]);
    }
  }, [selectedDistrict]);

  // Load talukas from API
  const loadTalukas = async () => {
    try {
      setLoadingTalukas(true);
      setError('');
      dismissAlert();

      const selectedDistrictObj = districts.find(d =>
        d.name === selectedDistrict || d.dist_name === selectedDistrict
      );

      if (!selectedDistrictObj) {
        console.error('Selected district not found:', selectedDistrict);
        return;
      }

      const distNo = selectedDistrictObj.dist_name;
      console.log('Loading talukas for distNo:', distNo);

      const response = await apiService.getTalukas(distNo);
      console.log('Talukas response:', response);

      const talukasData = response.data || response || [];
      const talukasArray = Array.isArray(talukasData) ? talukasData : [];
      setAvailableTalukas(talukasArray);

      if (talukasArray.length > 0) {
        showAlert('success', `Successfully loaded ${talukasArray.length} talukas for ${selectedDistrict}`, 3000);
      } else {
        showAlert('warning', `No talukas found for district: ${selectedDistrict}`);
      }

    } catch (error) {
      console.error('Error loading talukas:', error);
      setError('Failed to load talukas: ' + error.message);
      setAvailableTalukas([]);
    } finally {
      setLoadingTalukas(false);
    }
  };

  // Load villages when taluka changes
  useEffect(() => {
    if (selectedTaluka && selectedDistrict) {
      loadVillages();
    } else {
      setAvailableVillages([]);
      setSelectedVillage('');
    }
  }, [selectedTaluka, selectedDistrict]);

  // Load villages from API
  const loadVillages = async () => {
    try {
      setAvailableVillages([]);
      setLoadingVillages(true);
      setError('');
      dismissAlert();

      const selectedDistrictObj = districts.find(d =>
        d.name === selectedDistrict || d.dist_name === selectedDistrict
      );

      if (!selectedDistrictObj) {
        console.error('Selected district not found:', selectedDistrict);
        return;
      }

      const selectedTalukaObj = availableTalukas.find(t =>
        t.name === selectedTaluka || t.taluka_name === selectedTaluka || t.tehsil_name === selectedTaluka
      );

      if (!selectedTalukaObj) {
        console.error('Selected taluka not found:', selectedTaluka);
        return;
      }

      const distNo = selectedDistrictObj.dist_name;
      const tehsilNo = selectedTalukaObj.taluka_name;

      console.log('Loading villages for distNo:', distNo, 'tehsilNo:', tehsilNo);

      const response = await apiService.getVillages(distNo, tehsilNo);
      console.log('Villages response:', response);

      const villagesData = response.data || response || [];
      const villagesArray = Array.isArray(villagesData) ? villagesData : [];
      setAvailableVillages(villagesArray);

      if (villagesArray.length > 0) {
        showAlert('success', `Successfully loaded ${villagesArray.length} villages for ${selectedTaluka}`, 3000);
      } else {
        showAlert('warning', `No villages found for taluka: ${selectedTaluka}`);
      }

    } catch (error) {
      console.error('Error loading villages:', error);
      setError('Failed to load villages: ' + error.message);
      setAvailableVillages([]);
    } finally {
      setLoadingVillages(false);
    }
  };

  // Load land data when conditions are met
  useEffect(() => {
    if (projectId && proposalId) {
      if (filterMode === 'all') {
        loadLandDataForAll();
      } else if (filterMode === 'location' && selectedDistrict && selectedTaluka && selectedVillage) {
        loadLandData();
      } else if (filterMode === 'location') {
        setLandData([]);
      }
    } else {
      console.warn("Project ID and Proposal ID are required to load land data.");
      setLandData([]);
    }
  }, [filterMode, projectId, proposalId, selectedDistrict, selectedTaluka, selectedVillage]);

  // Load land data from API (for specific location)
  const loadLandData = async () => {
    if (!projectId || !proposalId) {
      console.warn("Missing projectId or proposalId — cannot load land data.");
      return;
    }

    try {
      setLoadingLandData(true);
      setError('');
      dismissAlert();

      const params = {
        projectId,
        proposalId,
        ownerId: null,
      };

      if (selectedDistrict) params.distName = selectedDistrict;
      if (selectedTaluka) params.talukaName = selectedTaluka;
      if (selectedVillage) params.villName = selectedVillage;

      console.log("📤 Sending params to getPaymentCompensation:", params);

      const response = await apiService.getPaymentCompensation(params);

      console.log("Owner compensation response:", response);

      if (response && response.status === 200 && response.data) {
        const landDataResponse = response.data.data || response.data;
        console.log('Extracted land data:', landDataResponse);

        if (Array.isArray(landDataResponse)) {
          setLandData(landDataResponse);
          console.log('Successfully set land data:', landDataResponse);

          if (landDataResponse.length > 0) {
            showAlert('success', `Successfully loaded ${landDataResponse.length} land records for ${selectedVillage}, ${selectedTaluka}`, 4000);
          } else {
            showAlert('info', `No land ownership records found for ${selectedVillage}, ${selectedTaluka} under Project ID: ${projectId}`);
          }
        } else {
          console.log('Land data is not an array:', landDataResponse);
          setLandData([]);
        }
      } else {
        console.log('Unexpected response structure:', response);
        setLandData([]);
        showAlert('info', `No land ownership data available for ${selectedVillage}, ${selectedTaluka} under Project ID: ${projectId}`);
      }

    } catch (error) {
      console.error('Error loading land data:', error);
      setLandData([]);
      if (error.message.includes('404') || error.message.includes('Not Found')) {
        showAlert('info', `No land ownership records found for the selected location (${selectedVillage}, ${selectedTaluka}) under Project ID: ${projectId}`);
      } else {
        setError('Failed to load payment data: ' + error.message);
      }
    } finally {
      setLoadingLandData(false);
    }
  };

  // Load ALL land data from API (regardless of location)
  const loadLandDataForAll = async () => {
    if (!projectId || !proposalId) {
      console.warn("Missing projectId or proposalId — cannot load land data.");
      return;
    }

    try {
      setLoadingLandData(true);
      setError('');
      dismissAlert();

      const params = {
        projectId,
        proposalId,
        ownerId: null,
      };

      console.log("📤 Sending params to getPaymentCompensation (all records):", params);

      const response = await apiService.getPaymentCompensation(params);

      console.log("Owner compensation response (all records):", response);

      if (response && response.status === 200 && response.data) {
        const landDataResponse = response.data.data || response.data;
        console.log('Extracted land data (all records):', landDataResponse);

        if (Array.isArray(landDataResponse)) {
          setLandData(landDataResponse);
          console.log('Successfully set all land data:', landDataResponse);

          if (landDataResponse.length > 0) {
            showAlert('success', `Successfully loaded ${landDataResponse.length} land records (all locations)`, 4000);
          } else {
            showAlert('info', `No land ownership records found for Project ID: ${projectId}`);
          }
        } else {
          console.log('Land data is not an array:', landDataResponse);
          setLandData([]);
        }
      } else {
        console.log('Unexpected response structure:', response);
        setLandData([]);
        showAlert('info', `No land ownership data available for Project ID: ${projectId}`);
      }

    } catch (error) {
      console.error('Error loading all land data:', error);
      setLandData([]);
      if (error.message.includes('404') || error.message.includes('Not Found')) {
        showAlert('info', `No land ownership records found for Project ID: ${projectId}`);
      } else {
        setError('Failed to load payment data: ' + error.message);
      }
    } finally {
      setLoadingLandData(false);
    }
  };

  // Filter data based on search criteria
  const filteredData = landData.filter(item => {
    const ownerName = item.owner_name || '';
    const ownerContact = item.owner_contact || '';
    const surveyNo = item.survey_no || '';

    const matchesSearch = searchTerm === '' ||
      ownerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ownerContact.toLowerCase().includes(searchTerm.toLowerCase()) ||
      surveyNo.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === '' ||
      (item.land_category || '') === filterCategory;

    return matchesSearch && matchesCategory;
  });

  // Handle view button click - getPaymentCompensationById
  const handleViewClick = async (pmtId) => {
    console.log('View button clicked for compensation ID:', pmtId);

    try {

      const paymentData = await apiService.getPaymentCompensationById(pmtId);

      console.log("Response from getPaymentCompensationById:", paymentData);

      if (paymentData.status === 200) {

        const recordToView = paymentData.data[0];

        console.log('Extracted payment record for viewing:', recordToView);

        setViewLandRecord(recordToView);

        setIsViewModalOpen(true);
        showAlert('success', `Viewing payment details for Compensation ID: ${pmtId}`, 3000);

      } else {
        console.log('Unexpected response structure or no data:', paymentData);
        showAlert('error', `Could not find payment details for ID: ${pmtId}`);
      }
    } catch (error) {
      console.error('Error fetching payment data for view:', error);
      setError('Failed to fetch payment details: ' + error.message);
      showAlert('error', `Failed to fetch payment details for ID: ${pmtId}`);
    }
  };

  // Handle view modal close
  const handleViewModalClose = () => {
    setIsViewModalOpen(false);
    setViewLandRecord(null);
  };

  const handleAddClick = (landRecord) => {
    const compensationData = {
      compensation_id: landRecord.compensation_id || null,
      land_owner_id: landRecord.land_owner_id,
      land_value_rate: landRecord.land_value_rate || 0,
      land_value_total: landRecord.land_value_total || 0,
      structure_value: landRecord.structure_value || 0,
      tree_value: landRecord.tree_value || 0,
      crop_value: landRecord.crop_value || 0,
      water_body_value: landRecord.water_body_value || 0,
      other_asset_value: landRecord.other_asset_value || 0,
      solatium_percentage: landRecord.solatium_percentage || 0,
      solatium_amount: landRecord.solatium_amount || 0,
      interest_rate: landRecord.interest_rate || 0,
      interest_amount: landRecord.interest_amount || 0,
      additional_compensation: landRecord.additional_compensation || 0,
      additional_compensation_reason: landRecord.additional_compensation_reason || '',
      total_compensation: landRecord.total_compensation || 0,
      currency: landRecord.currency || 'INR',
      compensation_status: landRecord.COMPENSATION_STATUS || landRecord.compensation_status || 'PENDING',
      valuation_date: landRecord.valuation_date || null,
      compensation_approval_date: landRecord.compensation_approval_date || null,
      compensation_approval_authority: landRecord.compensation_approval_authority || '',
      compensation_approval_reference: landRecord.compensation_approval_reference || '',
      payment_mode: landRecord.payment_mode || '',
      payment_reference: landRecord.payment_reference || '',
      payment_date: landRecord.payment_date || null,
      payment_remarks: landRecord.payment_remarks || '',
      consent_received: landRecord.consent_received || false,
      consent_date: landRecord.consent_date || null,
      cre_usr_id: landRecord.cre_usr_id || null,
      cre_usr_dt: landRecord.cre_usr_dt || null,
      last_upd_id: landRecord.last_upd_id || null,
      last_upd_dt: landRecord.last_upd_dt || null,
      project_id: landRecord.project_id || projectId,
      owner_name: landRecord.owner_name || '',
      bank_name: landRecord.bank_name || 'SBI',
      branch_name: landRecord.branch_name || 'Buldana',
      account_number: landRecord.account_number || '31010011425142',
      ifsc: landRecord.ifsc_code || landRecord.ifsc || 'SBIN0123456',
      account_holder_name: landRecord.account_holder_name || landRecord.owner_name || ''
    };

    console.log('Land Record:', landRecord);
    console.log('Extracted Compensation Data:', compensationData);
    console.log('Compensation ID being passed:', compensationData.compensation_id);

    if (compensationData.compensation_id) {
      console.log('✅ Compensation ID found:', compensationData.compensation_id);
    } else {
      console.log('⚠️ No compensation ID found - this will be a new compensation record');
    }

    setSelectedLandRecord(landRecord);
    setselectedcompensation(compensationData);
    setIsFormOpen(true);
  };

  const handleFormSubmitAndReload = (formData) => {
    console.log('Form submitted with data:', formData);
    console.log('For land record:', selectedLandRecord);

    showAlert('success', `Compensation form submitted successfully for ${selectedLandRecord?.owner_name}`, 4000);

    // Reload the data for the current filter mode
    if (filterMode === 'all') {
      loadLandDataForAll();
    } else {
      loadLandData();
    }

    setIsFormOpen(false);
    setSelectedLandRecord(null);
    setselectedcompensation(null);
  };

  // Handle form close
  const handleFormClose = () => {
    setIsFormOpen(false);
    setSelectedLandRecord(null);
    setselectedcompensation(null);
  };

  // Handle form submission
  const handleFormSubmit = (formData) => {
    console.log('Form submitted with data:', formData);
    console.log('For land record:', selectedLandRecord);

    showAlert('success', `Compensation form submitted successfully for ${selectedLandRecord?.owner_name}`, 4000);

    setIsFormOpen(false);
    setSelectedLandRecord(null);
    setselectedcompensation(null);
  };

  // Get display name for district
  const getDistrictDisplayName = (district) => {
    return district.dist_name || `District ${district.dist_name}`;
  };

  // Get display name for taluka
  const getTalukaDisplayName = (taluka) => {
    return taluka.taluka_name || `Taluka ${taluka.taluka_name}`;
  };

  // Get display name for village
  const getVillageDisplayName = (village) => {
    return village.village_name || `Village ${village.village_name}`;
  };

  // Helper function to get current location description
  const getCurrentLocationDescription = () => {
    if (filterMode === 'all') {
      return 'All Locations';
    }

    if (selectedDistrict && selectedTaluka && selectedVillage) {
      return `Village: ${selectedVillage}, ${selectedTaluka}, ${selectedDistrict}`;
    } else if (selectedDistrict && selectedTaluka) {
      return `Taluka: ${selectedTaluka}, ${selectedDistrict}`;
    } else if (selectedDistrict) {
      return `District: ${selectedDistrict}`;
    } else {
      return 'No location selected';
    }
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return 'N/A';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN');
    } catch (error) {
      return dateString;
    }
  };

  // Check if valid location is selected
  const hasValidLocationSelection = selectedDistrict && selectedTaluka && selectedVillage;
  const canShowData = filterMode === 'all' || (filterMode === 'location' && hasValidLocationSelection);


  const downloadBankReceipt = () => {
    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Create worksheet data array
    const wsData = [];

    // Column headers (starting from first row)
    wsData.push([
      'Sr.No',
      'Description',
      'Name as per Pass-Book',
      'Account No.',
      'IFSC Code',
      'Bank Name & Branch',
      'Net Amount (Rs.)',
      'Transaction Date',
      'Whether Transaction done Successfully (Yes/No)',
      'If Yes, UTR No. (Journal No. For SBI)',
      'If No., Reason'
    ]);

    // Sample row with LAC details
    // wsData.push([
    //   1,
    //   LAC No. ------\n10 ------ DIST ------\nAWARD Sr.NO. ----,
    //   '………..',
    //   '………….',
    //   '………….',
    //   '……………',
    //   '……….',
    //   '………….',
    //   '………….',
    //   '……………',
    //   '……….'
    // ]);

    // Add actual payment data if available
    if (filteredData && filteredData.length > 0) {
      filteredData.forEach((item, index) => {
        wsData.push([
          index + 1,
          `Survey No: ${item.survey_no || 'N/A'}\nOwner ID: ${item.land_owner_id || 'N/A'}`,
          item.owner_name || 'N/A',
          item.account_number || 'N/A',
          item.ifsc_code || item.ifsc || 'N/A',
          `${item.bank_name || 'N/A'}\n${item.branch_name || 'N/A'}`,
          item.paid_amount || item.total_compensation || 'N/A',
          item.paid_date ? new Date(item.paid_date).toLocaleDateString('en-IN') : 'N/A',
          item.COMPENSATION_STATUS === 'Completed' ? 'Yes' : 'No',
          item.paid_refrence_no || 'N/A',
          item.payment_remarks || ''
        ]);
      });
    }

    // Add empty rows for manual entry
    for (let i = 0; i < 10; i++) {
      wsData.push(['', '', '', '', '', '', '', '', '', '', '']);
    }

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Set column widths
    ws['!cols'] = [
      { wch: 6 },   // Sr.No
      { wch: 10 },  // Description
      { wch: 20 },  // Name as per Pass-Book
      { wch: 11 },  // Account No.
      { wch: 9 },  // IFSC Code
      { wch: 18 },  // Bank Name & Branch
      { wch: 14 },  // Net Amount
      { wch: 14 },  // Transaction Date
      { wch: 40 },  // Whether Transaction done
      { wch: 30 },  // UTR No.
      { wch: 14 }   // Reason
    ];

    // Set row heights
    ws['!rows'] = [
      { hpx: 50 },  // Column headers row height (now first row)
    ];

    // Apply styling
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell_address = { c: C, r: R };
        const cell_ref = XLSX.utils.encode_cell(cell_address);

        if (!ws[cell_ref]) {
          ws[cell_ref] = { t: 's', v: '' };
        }

        // Apply borders to all cells
        ws[cell_ref].s = {
          border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } }
          },
          alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true
          }
        };

        if (R === 0) {
          // Column headers styling (now first row)
          ws[cell_ref].s.font = { bold: true, sz: 11 };
          ws[cell_ref].s.fill = { fgColor: { rgb: 'D3D3D3' } };
        }
      }
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Bank Receipt');

    // Generate filename with current date and project info
    const currentDate = new Date().toLocaleDateString('en-IN').replace(/\//g, '-');
    const locationInfo = getCurrentLocationDescription().replace(/[^a-zA-Z0-9]/g, '_');
    const filename = `Bank_Receipt_${locationInfo}_${currentDate}.xlsx`;

    // Download the file
    XLSX.writeFile(wb, filename);

    // Show success message
    showAlert('success', 'Bank receipt downloaded successfully!', 3000);
  };

  if (loadingLandData && landData.length === 0) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex justify-center items-center h-80">
          <div className="text-center animate-float">
            <div className="inline-flex relative">
              <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-transparent animate-spin"></div>
              <div className="w-16 h-16 rounded-full border-4 border-purple-500 border-b-transparent border-r-transparent animate-spin absolute inset-0" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <FileText className="h-6 w-6 text-purple-400 animate-pulse" />
              </div>
            </div>
            <p className="text-gray-400 mt-4">{translations[language].loadingPaymentData}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 shadow-sm m-2 rounded-xl">
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <h1 className="text-xl font-bold text-gray-800">{translations[language].paymentManagement}</h1>
                <p className="text-xs text-gray-600 mt-0.5">
                  {currentProjectName && `Project: ${currentProjectName}`}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={downloadBankReceipt}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md text-sm bg-green-600 text-white hover:bg-green-700"
              >
                <Download className="h-4 w-4" />
                <span>Download Bank Receipt</span>
              </button>

              {/* Filter Button */}
              {/* <button
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md text-sm ${showFilterPanel
                  ? 'bg-orange-600 text-white'
                  : 'bg-orange-600 text-white'
                  }`}
              >
                <Filter className="h-3.5 w-3.5" />
                <span>{translations[language].filters}</span>
                {showFilterPanel ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </button> */}
            </div>
          </div>
        </div>
      </div>

      {/* Collapsible Filter Panel */}
      {showFilterPanel && (
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="p-6">
            <div className="space-y-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                {/* Filter Mode Selection */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1 bg-gray-100 rounded-xl p-1.5 shadow-inner">
                    <button
                      onClick={() => setFilterMode('all')}
                      className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${filterMode === 'all'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                        }`}
                    >
                      <Globe className="h-4 w-4" />
                      <span>{translations[language].allRecords}</span>
                    </button>
                    <button
                      onClick={() => setFilterMode('location')}
                      className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${filterMode === 'location'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                        }`}
                    >
                      <MapPin className="h-4 w-4" />
                      <span>{translations[language].filterByLocation}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Location Filters - Only show when location mode is selected */}
              {filterMode === 'location' && (
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <MapPin className="h-5 w-5 text-blue-600 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-800">{translations[language].selectLocation}</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {translations[language].district} <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={selectedDistrict}
                          onChange={(e) => setSelectedDistrict(e.target.value)}
                          disabled={loadingDistricts}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                        >
                          <option value="">
                            {loadingDistricts ? <span>{translations[language].loadingDistricts}</span> : <span>{translations[language].selectDistrict}</span>}
                          </option>
                          {districts.map(district => (
                            <option key={district.dist_name} value={getDistrictDisplayName(district)}>
                              {getDistrictDisplayName(district)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {translations[language].taluka} <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={selectedTaluka}
                          onChange={(e) => setSelectedTaluka(e.target.value)}
                          disabled={!selectedDistrict || loadingTalukas}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                        >
                          <option value="">
                            {loadingTalukas ? <span>{translations[language].loadingTalukas}</span> : <span>{translations[language].selectTaluka}</span>}
                          </option>
                          {availableTalukas.map(taluka => (
                            <option key={taluka.taluka_name} value={getTalukaDisplayName(taluka)}>
                              {getTalukaDisplayName(taluka)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {translations[language].village} <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={selectedVillage}
                          onChange={(e) => setSelectedVillage(e.target.value)}
                          disabled={!selectedTaluka || loadingVillages}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                        >
                          <option value="">
                            {loadingVillages ? <span>{translations[language].loadingVillages}</span> : <span>{translations[language].selectVillage}</span>}
                          </option>
                          {availableVillages.map(village => (
                            <option key={village.village_name} value={getVillageDisplayName(village)}>
                              {getVillageDisplayName(village)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {/* Show helpful message about progressive filtering */}
                    {filterMode === 'location' && !hasValidLocationSelection && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <p className="text-sm text-amber-700">
                          {translations[language].selectFiltersToViewPayment}
                        </p>
                      </div>
                    )}
                    {filterMode === 'location' && hasValidLocationSelection && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm text-green-700 flex items-center">
                          <span className="mr-2">✓</span>
                          {translations[language].showingDataFor}: {getCurrentLocationDescription()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mx-6 mb-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-3">
              <button
                onClick={filterMode === 'all' ? loadLandDataForAll : loadLandData}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors shadow-sm"
              >
                {translations[language].retry}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Only show when data can be displayed */}
      {canShowData && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gradient-to-r from-gray-100 to-gray-200">
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold text-gray-700">{translations[language].Actions}</th>
                    <th className="px-4 py-4 text-left font-semibold text-gray-700">{translations[language].occupantName}</th>
                    <th className="px-4 py-4 text-left font-semibold text-gray-700">{translations[language].occupantType}</th>
                    <th className="px-4 py-4 text-left font-semibold text-gray-700">{translations[language].contact}</th>
                    <th className="px-4 py-4 text-left font-semibold text-gray-700">{translations[language].surveyNo}</th>
                    <th className="px-4 py-4 text-left font-semibold text-gray-700">{translations[language].location}</th>
                    <th className="px-4 py-4 text-left font-semibold text-gray-700">{translations[language].accountNumber}</th>
                    <th className="px-4 py-4 text-left font-semibold text-gray-700">{translations[language].ifscCode}</th>
                    <th className="px-4 py-4 text-left font-semibold text-gray-700">{translations[language].compensationValue}</th>
                    <th className="px-4 py-4 text-left font-semibold text-gray-700">{translations[language].status}</th>
                  </tr>
                </thead>

                <tbody className="bg-white">
                  {loadingLandData ? (
                    <tr>
                      <td colSpan="9" className="px-6 py-12 text-center">
                        <div className="p-6 max-w-7xl mx-auto">
                          <div className="flex justify-center items-center h-80">
                            <div className="text-center animate-float">
                              <div className="inline-flex relative">
                                <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-transparent animate-spin"></div>
                                <div className="w-16 h-16 rounded-full border-4 border-purple-500 border-b-transparent border-r-transparent animate-spin absolute inset-0" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <FileText className="h-6 w-6 text-purple-400 animate-pulse" />
                                </div>
                              </div>
                              <p className="text-gray-400 mt-4">{translations[language].LoadingPaymentData}</p>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : filteredData.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center space-y-3">
                          {/* <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                            <FileBadge2 className="w-8 h-8 text-gray-400" />
                          </div> */}
                          <div className="text-red-500 font-medium">
                            {filterMode === 'location' && !hasValidLocationSelection
                              ? <span>{translations[language].PleaseSelectDistrictTalukaVillageToLoadPaymentData}</span>
                              : <span>{translations[language].stageError}</span>
                            }
                          </div>
                          {/* {((filterMode === 'location' && hasValidLocationSelection) || filterMode === 'all') && projectId && (
                            <div className="text-xs text-gray-400">
                              {translations[language].projectId}: {projectId}
                              {proposalId && ` | Proposal ID: ${proposalId}`}
                              <br />
                              {translations[language].location}: {getCurrentLocationDescription()}
                            </div>
                          )} */}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((item, index) => (
                      <tr
                        key={item.land_owner_id || item.LandAcquisition_id || index}
                        className={`hover:bg-blue-50/50 transition-colors duration-150 border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          }`}
                      >
                        <td className="px-4 py-4">
                          {item.payment_done === true ? (
                            <button
                              onClick={() => handleViewClick(item.compensation_id)}
                              className="flex items-center space-x-1 px-3 py-1 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm"
                            >
                              <Eye size={14} />
                              <span>{translations[language].view}</span>
                            </button>
                          ) : (
                            userRole === ROLES.LAO && (
                              <button
                                onClick={() => handleAddClick(item)}
                                className={`flex items-center px-3 py-1 rounded-lg transition-colors text-sm font-medium shadow-sm ${item.compensation_id && item.compensation_id !== null && item.compensation_id !== ''
                                  ? 'bg-green-600 text-white hover:bg-green-700'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                                  }`}
                              >
                                {item.compensation_id && item.compensation_id !== null && item.compensation_id !== '' ? (
                                  <>
                                    <span>{translations[language].updatePayment}</span>
                                  </>
                                ) : (
                                  <>
                                    <Plus size={14} />
                                    <span>{translations[language].addCompensation}</span>
                                  </>
                                )}
                              </button>
                            )
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div>
                            <div className="font-medium text-gray-900">{item.owner_name || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{translations[language].ID}: {item.land_owner_id}</div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="inline-flex px-2 py-1 text-medium">
                            {item.owner_type || 'Individual'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-gray-700">{item.owner_contact || 'N/A'}</td>
                        <td className="px-4 py-4 font-medium text-blue-600 font-mono">{item.survey_no || 'N/A'}</td>
                        <td className="px-4 py-4">
                          <div className="text-gray-700">{item.village_name || 'N/A'}, </div>
                          <div className="text-xs text-gray-500">
                            {item.taluka_name || 'N/A'}, {item.dist_name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-gray-700">
                          {item.account_number || 'N/A'}
                        </td>
                        <td className="px-4 py-4 text-gray-700">
                          {item.ifsc_code || 'N/A'}
                        </td>

                        <td className="px-4 py-4">
                          <div className="font-semibold text-green-600">
                            {item.total_compensation
                              ? `₹ ${item.total_compensation.toLocaleString('en-IN')}`
                              : item.land_value_total
                                ? `₹ ${item.land_value_total.toLocaleString('en-IN')}`
                                : 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.payment_done === true
                              ? 'bg-green-100 text-green-800'
                              : item.payment_done === false
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-gray-100 text-gray-800'
                              }`}
                          >
                            {item.payment_done === true ? 'Completed' : 'Pending'}
                          </span>
                        </td>

                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary */}
          {/* <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600">
              Showing {filteredData.length} of {landData.length} records
            </p>
            <div className="mt-2">
              <p className="text-sm text-gray-700 font-medium">Current View: {getCurrentLocationDescription()}</p>
            </div>
          </div> */}
        </div>
      )}

      {/* Show message when filter mode is location but selection is incomplete */}
      {filterMode === 'location' && !hasValidLocationSelection && (
        <div className="mt-6 text-center py-12">
          <div className="max-w-md mx-auto">
            <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg font-medium mb-2">{translations[language].locationSelectionRequired}</p>
            <p className="text-gray-400 text-sm">
              {translations[language].selectFiltersToViewPaymentAlt}
            </p>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden border border-gray-200">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 flex justify-between items-center shadow-lg">
              <div>
                <h2 className="text-xl font-bold text-white mb-1">{translations[language].paymentDetails}</h2>
                <p className="text-blue-100 text-sm">
                  {translations[language].occupantName} : {selectedLandRecord?.owner_name}
                </p>
              </div>
              <button
                onClick={handleFormClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-all duration-200 text-white hover:rotate-90"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="overflow-auto max-h-[calc(90vh-60px)]">
              <CompensationForm
                landRecord={selectedLandRecord}
                onSubmit={handleFormSubmitAndReload}
                onClose={handleFormClose}
                compensation={selectedcompensation}
              />
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {isViewModalOpen && viewLandRecord && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden border border-gray-200">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 flex justify-between items-center shadow-lg">
              <div>
                <h2 className="text-xl font-bold text-white mb-1">{translations[language].paymentDetails}</h2>
                {/* <p className="text-blue-100 text-sm">
                  {landData?.owner_name} (ID: {viewLandRecord?.land_owner_id})
                </p> */}
              </div>
              <button
                onClick={handleViewModalClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-all duration-200 text-white hover:rotate-90"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-3 overflow-auto max-h-[calc(95vh-80px)]">
              <div className="space-y-3">

                {/* Payment Information */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                  <div className="flex items-center mb-4">
                    <Building className="h-6 w-6 text-purple-600 mr-3" />
                    <h3 className="text-lg font-semibold text-gray-800">{translations[language].paymentInformation}</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">{translations[language].amount}</label>
                      <p className="text-gray-900 font-semibold">₹ {viewLandRecord.paid_amount || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">{translations[language].paymentMode}</label>
                      <p className="text-gray-900 font-semibold">{viewLandRecord.paid_mode || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">{translations[language].paymentDate}</label>
                      <p className="text-gray-900 font-semibold">{formatDate(viewLandRecord.paid_date)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">{translations[language].paymentReference}</label>
                      <p className="text-gray-900 font-semibold">{viewLandRecord.paid_refrence_no || 'N/A'}</p>
                    </div>
                    <div >
                      <label className="block text-sm font-medium text-gray-500 mb-1">{translations[language].paymentRemarks}</label>
                      <p className="text-gray-900 font-semibold">{viewLandRecord.payment_remarks || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Bank Details */}
                <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-6 border border-yellow-200">
                  <div className="flex items-center mb-4">
                    <Building className="h-6 w-6 text-indigo-600 mr-3" />
                    <h3 className="text-lg font-semibold text-gray-800">{translations[language].toBankDetails}</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">{translations[language].bankName}</label>
                      <p className="text-gray-900 font-semibold">{viewLandRecord.bank_name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">{translations[language].branchName}</label>
                      <p className="text-gray-900 font-semibold">{viewLandRecord.branch_name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">{translations[language].accountNumber}</label>
                      <p className="text-gray-900 font-semibold">{viewLandRecord.account_number || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">{translations[language].ifscCode}</label>
                      <p className="text-gray-900 font-semibold">{viewLandRecord.ifsc_code || viewLandRecord.ifsc || 'N/A'}</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-500 mb-1">{translations[language].accountHolderName}</label>
                      <p className="text-gray-900 font-semibold">{viewLandRecord.account_holder_name || viewLandRecord.owner_name || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* From bank details */}
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-200">
                  <div className="flex items-center mb-4">
                    <Building className="h-6 w-6 text-indigo-600 mr-3" />
                    <h3 className="text-lg font-semibold text-gray-800">{translations[language].fromBankDetails}</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">{translations[language].accountNumber}</label>
                      <p className="text-gray-900 font-semibold">{viewLandRecord.paid_from_ac_no || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">{translations[language].ifscCode}</label>
                      <p className="text-gray-900 font-semibold">{viewLandRecord.paid_from_bank_ISFC || viewLandRecord.ifsc || 'N/A'}</p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Payment;